import { uploadPOAAndPOIProof } from './uploadUtils';
import { ImageStore } from './userImageUploadUtils';

export const generateUniqueId = () => {
   const crypto = window.crypto || (window as any).msCrypto; // For IE 11 support
   const array = new Uint8Array(16);
   crypto.getRandomValues(array);

   // Set the version to 4 (UUID version 4)
   array[6] = (array[6] & 0x0f) | 0x40;
   // Set the variant to RFC 4122
   array[8] = (array[8] & 0x3f) | 0x80;

   const toHex = (num: number): string => num.toString(16).padStart(2, '0');

   const interactionId = `${toHex(array[0])}${toHex(array[1])}${toHex(array[2])}${toHex(
      array[3]
   )}-${toHex(array[4])}${toHex(array[5])}-${toHex(array[6])}${toHex(array[7])}-${toHex(
      array[8]
   )}${toHex(array[9])}-${toHex(array[10])}${toHex(array[11])}${toHex(array[12])}${toHex(
      array[13]
   )}${toHex(array[14])}${toHex(array[15])}`;

   return interactionId;
};

export const generateInteractionId = () => {
   let interactionId = sessionStorage.getItem('interactionId');

   if (interactionId) {
      return interactionId;
   }

   interactionId = generateUniqueId();
   sessionStorage.setItem('interactionId', interactionId);
   return interactionId;
};

export const storeInteractionId = (onUserAction: any) => {
   return onUserAction?.({
      actions: [
         {
            type: 'storeValuesInRedux',
            payload: {
               context: 'skycInteractionId',
               data: {
                  interactionId: generateInteractionId(),
               },
            },
         },
      ],
   });
};

export const generateChangeActions = ({ args, data, onUserAction, getState }: any) => {
   const currStepName = args?.[0]?.stepName as string;
   const { storeValues } = getState();
   const isNewSKYCJourney = storeValues?.journeyContext?.journey === 'SKYC_NEW';

   const stepsName = [
      'step1Widget',
      'step2Widget',
      'step3Widget',
      'step4Widget',
      'step5Widget',
      'step6Widget',
      'step7Widget',
      'DISABILITY_INFO',
      'LOCAL_REFERENCE_DETAILS',
      'CURRENT_ADDRESS_DETAILS',
      'IDENTITY_PROOF',
      'ServiceSelectionWidget',
      'UserDetailsWidget',
      'PortingDetailsWidget',
      'PlanListWidget',
      'NumberSelectionWidget',
      'IntentSelection',
      'SimScannerWidget',
   ];

   const actions: any = stepsName.map((eventName) => {
      if (eventName !== currStepName) {
         return {
            type: 'nativeEventEmitter',
            payload: {
               eventName: eventName,
               eventData: {
                  isExpanded: false,
               },
            },
         };
      }

      return {
         type: 'nativeEventEmitter',
         payload: {
            eventName: currStepName,
            eventData: {
               isExpanded: true,
            },
         },
      };
   });

   let eventValueMap: { [key: string]: string } = {
      step2Widget: 'aadhar number',
      step3Widget: 'details confirmation',
      step4Widget: 'photo verification',
      step5Widget: 'video verification',
      step6Widget: 'SIM declaration',
      DISABILITY_INFO: 'disability status',
      CURRENT_ADDRESS_DETAILS: 'current address details',
      LOCAL_REFERENCE_DETAILS: 'local reference details',
      PortingDetailsWidget: 'porting details',
      PlanListWidget: 'postpaid plan',
      UserDetailsWidget: 'service selection',
   };

   const eventValue = eventValueMap[currStepName];
   let pathName = window?.location?.pathname;
   if (isNewSKYCJourney) {
      actions?.push({
         analyticsAction: 'click',
         analyticsData: {
            eventCategory: pathName?.includes('kyc/verification')
               ? 'NEW_ACQ_POSTPAID_SKYC'
               : 'NEW_ACQ_POSTPAID_SKYC_LANDING_PAGE',
            section: 'stepsSection',
            subSection: currStepName,
            eventAction: 'click',
            eventLabel: 'change',
            eventValue,
            customValue: '',
            customLabel: 'button',
            prop35: '{{storeValues:orderData.orderData.cartId}}',
            extraData12: '{{storeValues:orderData.orderData.cartItemId}}',
            prop45: '{{storeValues:appData.subJourney}}',
            eVar21: '{{storeValues:appData.loggedInNumber}}',
            eVar36: '{{storeValues:appData.msisdn}}',
            city: '{{storeValues:pincodeContext.pincodeDetails.city}}',
            eVar25: '{{storeValues:pincodeContext.pincodeDetails.circleName}}',
            prop4: 'SKYC',
            extraData11: '{{storeValues:layoutContext.layout}}',
            prop7: '{{storeValues:outstationContext.isOutstation}}',
            prop9: '{{storeValues:appData.lob}}',
         },
      });
   }

   return onUserAction({
      actions: actions,
   });
};

export const handleUpdateDob = ({ additionalData, onUserAction, getState }: any) => {
   const { storeValues } = getState();
   const { value, dob } = additionalData?.dataFromWidget ?? {};
   const { DobUpdateStatus, skycInteractionId, journeyContext = {} } = storeValues ?? {};
   const isAllowed = DobUpdateStatus?.isUpdated ?? false;
   const interactionId = skycInteractionId?.interactionId;
   const journey = journeyContext?.journey;
   const formattedDob = dob.split('-').reverse().join('-');

   if (isAllowed) {
      return onUserAction({
         actions: [
            {
               payload: {
                  requestType: 'POST',
                  apiUrl: 'guardian/api/skyc/updateDOB/v1',
                  authTokenGroup: 'SKYC_REVERIFICATION',
                  body: {
                     interactionId: interactionId,
                     dateOfBirth: dob,
                  },
               },
               type: 'apiCall',
            },
            {
               analyticsAction: 'click',
            },
            {
               type: 'storeValuesInRedux',
               payload: {
                  context: 'kycDetails',
                  data: {
                     guardianName: value,
                     dob,
                  },
               },
            },
         ],
      });
   } else {
      return onUserAction({
         actions: [
            {
               type: 'storeValuesInRedux',
               payload: {
                  context: 'kycDetails',
                  data: {
                     guardianName: value,
                     dob: journey === 'SKYC_NEW' ? formattedDob : dob,
                  },
               },
            },
            {
               payload: {
                  eventData: {
                     isExpanded: false,
                     state: 'success',
                  },
                  eventName: 'step3Widget',
               },
               type: 'nativeEventEmitter',
            },
            {
               analyticsAction: 'click',
               analyticsData: {
                  eventCategory: 'SKYC_LANDING_PAGE',
                  section: 'stepssection',
                  subSection: 'Step3Widget',
                  eventAction: 'pageclose',
                  eVar21: '{{storeValues:appData.msisdn}}',
                  extraData11: '{{storeValues:layoutContext.layout}}',
                  prop8: 'reverification',
                  prop6: '{{storeValues:siNumberStatusDetails.actionRequired}}',
                  prop7: '{{storeValues:siNumberStatusDetails.customerZone}}',
                  prop5: '{{storeValues:outstationContext.isOutstation}}',
               },
            },
            {
               payload: {
                  eventData: {
                     isExpanded: true,
                     state: 'none',
                  },
                  eventName: 'step4Widget',
               },
               type: 'nativeEventEmitter',
            },
            {
               analyticsAction: 'click',
            },
         ],
      });
   }
};

export const handleValidSimDeclaration = ({
   args,
   additionalData,
   onUserAction,
   getState,
}: any) => {
   const {
      providerDetails,
      providerDetailsText,
      simOwnerDetails,
      selectedTotalConnectionCount,
      multipleConnectionReason,
   } = additionalData?.dataFromWidget ?? {};
   const { storeValues } = getState();
   const { isNESACircle, isJKCircle } = storeValues?.siNumberStatusDetails || {};
   const isNewSKYCJourney = storeValues?.journeyContext?.journey === 'SKYC_NEW';

   const actions: any[] = [
      {
         payload: {
            eventData: {
               state: 'success',
               isExpanded: false,
            },
            eventName: 'step6Widget',
         },
         type: 'nativeEventEmitter',
      },
      {
         payload: {
            data: {
               providerDetailsText,
               simOwnerDetails: simOwnerDetails?.length ? simOwnerDetails : [],
               selectedTotalConnectionCount,
               multipleConnectionReason,
            },
            context: 'simDeclarationWidgetData',
         },
         type: 'storeValuesInRedux',
      },
      {
         payload: {
            data: {
               totalCount: selectedTotalConnectionCount,
               connections:
                  selectedTotalConnectionCount > 0
                     ? providerDetails?.map(
                          ({ provider, count }: { provider: string; count: number }) => ({
                             operator: provider,
                             count: count?.toString(),
                          })
                       )
                     : [],
            },
            context: 'otherConnections',
         },
         type: 'storeValuesInRedux',
      },
   ];

   if (isNESACircle || isJKCircle) {
      actions.push({
         payload: {
            eventData: {
               state: 'none',
               isExpanded: true,
            },
            eventName: 'step7Widget',
         },
         type: 'nativeEventEmitter',
      });
   } else {
      actions.push({
         payload: {
            eventData: {
               state: 'none',
               isExpanded: true,
            },
            eventName: 'DISABILITY_INFO',
         },
         type: 'nativeEventEmitter',
      });
      if (isNewSKYCJourney) {
         actions?.push({
            analyticsAction: 'impression',
            analyticsData: {
               eventCategory: 'NEW_ACQ_POSTPAID_SKYC',
               section: 'stepsSection',
               subSection: 'DISABILITY_INFO',
               eventAction: 'pageloaded',
               eventLabel: '',
               eventValue: '',
               customValue: '',
               customLabel: '',
               prop35: '{{storeValues:orderData.orderData.cartId}}',
               extraData12: '{{storeValues:orderData.orderData.cartItemId}}',
               prop45: '{{storeValues:appData.subJourney}}',
               eVar21: '{{storeValues:appData.loggedInNumber}}',
               eVar36: '{{storeValues:appData.msisdn}}',
               city: '{{storeValues:pincodeContext.pincodeDetails.city}}',
               eVar25: '{{storeValues:pincodeContext.pincodeDetails.circleName}}',
               prop4: 'SKYC',
               extraData11: '{{storeValues:layoutContext.layout}}',
               prop7: '{{storeValues:outstationContext.isOutstation}}',
               prop9: '{{storeValues:appData.lob}}',
            },
         });
      } else {
         actions.push({
            analyticsAction: 'click',
            analyticsData: {
               eventCategory: 'SKYC_LANDING_PAGE',
               section: 'stepssection',
               subSection: 'DISABILITY_INFO',
               eventAction: 'pageloaded',
               eVar21: '{{storeValues:appData.msisdn}}',
               extraData11: '{{storeValues:layoutContext.layout}}',
               prop8: 'reverification',
               prop6: '{{storeValues:siNumberStatusDetails.actionRequired}}',
               prop5: '{{storeValues:outstationContext.isOutstation}}',
            },
         });
      }
   }

   onUserAction?.({
      actions,
   });
};

export const prepareDisabilityValidFormMetaData = ({
   additionalData,
   onUserAction,
   getState,
}: any) => {
   const { frontFile, disability, backFile, dropdownValue } =
      additionalData?.dataFromWidget ?? {};
   const { storeValues } = getState();
   const { siNumberStatusDetails, outstationContext, journeyContext } = storeValues ?? {};
   const { actionRequired } = siNumberStatusDetails || {};
   const { isOutstation } = outstationContext || {};
   const { journey } = journeyContext || {};

   const actions: any[] = [
      {
         payload: {
            eventData: {
               state: 'success',
               isExpanded: false,
            },
            eventName: 'DISABILITY_INFO',
         },
         actionWithDelayed: 100,
         type: 'nativeEventEmitter',
      },
      {
         payload: {
            data: {
               frontFile: frontFile,
               disability: disability,
               backFile: backFile,
               dropdownValue: dropdownValue,
            },
            context: 'disabilityWidgetData',
         },
         type: 'storeValuesInRedux',
      },
   ];

   if (!isOutstation && actionRequired === 'FULL_KYC') {
      const leftCta = {
         disabled: false,
         size: 'md',
         buttonType: 'primary',
         title: 'Verify Via OTP & Submit',
      };
      if (journey === 'SKYC_NEW') {
         actions.push(
            {
            payload: {
               eventData: {
                  leftCta: {
                     ...leftCta,
                     title: 'Save & Review Details',
                  },
               },
               eventName: 'FinalSubmitButton_submitButton',
            },
            type: 'nativeEventEmitter',
         }, 
         {
            type: 'nativeEventEmitter',
            payload:{
               eventData:{
                 checkboxData: {
                   label: 'I declare that the above information is true',
                   actionCta: 'Read More',
                 },
                 onActionCtaPressMetaData: {
                  id: 'onActionPress',
                  actions: [
                     {
                       payload: {
                         modalData: [],
                         modalSection: 'FinalTNCModalSection',
                       },
                       type: 'openModal',
                     },
                     {
                        analyticsAction: "click",
                        analyticsData: {
                           eVar21: "{{storeValues:appData.loggedInNumber}}",
                           eventCategory: "NEW_ACQ_POSTPAID_SKYC",
                           city: "{{storeValues:pincodeContext.pincodeDetails.city}}",
                           eVar36: "{{storeValues:appData.msisdn}}",
                           eVar25: "{{storeValues:pincodeContext.pincodeDetails.circleName}}",
                           section: "FinalSubmitButton",
                           eventAction: "click",
                           eventLabel: "readmore",
                           eventValue: "",
                           prop4: "SKYC",
                           extraData11: "{{storeValues:layoutContext.layout}}",
                           extraData12: "{{storeValues:orderData.orderData.cartItemId}}",
                           subSection: "submitButton",
                           customLabel: "button",
                           customValue: "",
                           prop35: "{{storeValues:orderData.orderData.cartId}}",
                           prop45: "{{storeValues:appData.subJourney}}",
                           prop7: "{{storeValues:outstationContext.isOutstation}}",
                           prop9: "{{storeValues:appData.lob}}"
                        }
                     }
                  ],
                },
               'buttonData.leftCta.title': 'Save & Review Details',
               },
               eventName: 'FinalSubmitButton_submitButton',
            }
         }
      
      );
      } else {
         actions.push({
            payload: {
               eventData: {
                  leftCta,
               },
               eventName: 'FinalSubmitButton_submitButton',
            },
            type: 'nativeEventEmitter',
         });
         actions.push({
            analyticsAction: 'click',
            analyticsData: {
               eventCategory: 'SKYC_LANDING_PAGE', // TODO
               section: 'FinalSubmitButton',
               subSection: 'submitButton',
               eventAction: 'enabled',
               eventLabel: 'verify',
               eventValue: 'Verify Via OTP & Submit',
               customLabel: 'final verification',
               eVar21: '{{storeValues:appData.msisdn}}',
               extraData11: '{{storeValues:layoutContext.layout}}',
               prop8: 'reverification',
               prop6: '{{storeValues:siNumberStatusDetails.actionRequired}}',
               prop7: '{{storeValues:siNumberStatusDetails.customerZone}}',
               prop5: '{{storeValues:outstationContext.isOutstation}}',
            },
         });
      }
   } else {
      actions.push({
         payload: {
            eventData: {
               state: 'none',
               isExpanded: true,
            },
            eventName: 'CURRENT_ADDRESS_DETAILS',
         },
         type: 'nativeEventEmitter',
      });
      if (journey === 'SKYC_NEW') {
         actions?.push({
            analyticsAction: 'impression',
            analyticsData: {
               eventCategory: 'NEW_ACQ_POSTPAID_SKYC',
               section: 'stepsSection',
               subSection: 'CURRENT_ADDRESS_DETAILS',
               eventAction: 'pageloaded',
               eventLabel: '',
               eventValue: '',
               customValue: '',
               customLabel: '',
               prop35: '{{storeValues:orderData.orderData.cartId}}',
               extraData12: '{{storeValues:orderData.orderData.cartItemId}}',
               prop45: '{{storeValues:appData.subJourney}}',
               eVar21: '{{storeValues:appData.loggedInNumber}}',
               eVar36: '{{storeValues:appData.msisdn}}',
               city: '{{storeValues:pincodeContext.pincodeDetails.city}}',
               eVar25: '{{storeValues:pincodeContext.pincodeDetails.circleName}}',
               prop4: 'SKYC',
               extraData11: '{{storeValues:layoutContext.layout}}',
               prop7: '{{storeValues:outstationContext.isOutstation}}',
               prop9: '{{storeValues:appData.lob}}',
            },
         });
      } else {
         actions.push({
            analyticsAction: 'click',
            analyticsData: {
               eventCategory: 'SKYC_LANDING_PAGE', // TODO
               section: 'stepssection',
               subSection: 'CURRENT_ADDRESS_DETAILS',
               eventAction: 'pageloaded',
               eVar21: '{{storeValues:appData.msisdn}}',
               extraData11: '{{storeValues:layoutContext.layout}}',
               prop8: 'reverification',
               prop7: '{{storeValues:siNumberStatusDetails.customerZone}}',
               prop6: '{{storeValues:siNumberStatusDetails.actionRequired}}',
               prop5: '{{storeValues:outstationContext.isOutstation}}',
            },
         });
      }
   }
  return actions
}

export const handleValidDisabilityInfo = ({
   additionalData,
   onUserAction,
   getState,
}: any) => {
   const { disability} = additionalData?.dataFromWidget ?? {};

   if(disability?.toLowerCase() === 'yes') return ;

   const actions = prepareDisabilityValidFormMetaData({
      additionalData,
      onUserAction,
      getState,
   }) 

   onUserAction?.({
      actions,
   });
};

export const handleValidLocalReferenceInfo = ({
   additionalData,
   onUserAction,
   getState,
}: any) => {
   const {
      areaInput,
      pinCodeInput,
      cityInput,
      streetInput,
      mobileNumberInput,
      districtInput,
      nameInput,
      houseNoInput,
      stateInput,
      landmarkInput,
   } = additionalData?.dataFromWidget ?? {};
   const { storeValues } = getState();
   const { isJKCircle } = storeValues?.siNumberStatusDetails || {};

   const actions: any[] = [
      {
         payload: {
            data: {
               area: areaInput,
               pincode: pinCodeInput,
               city: cityInput,
               street: streetInput,
               mobileNumber: mobileNumberInput,
               district: districtInput,
               name: nameInput,
               houseNo: houseNoInput,
               state: stateInput,
               landmark: landmarkInput,
            },
            context: 'localReferenceDetails',
         },
         type: 'storeValuesInRedux',
      },
      {
         payload: {
            args: [
               {
                  isReferee: true,
               },
            ],
            utilName: 'callSendRefereeAndSubmitOtp',
         },
         type: 'callUtilFunction',
      },
   ];

   if (isJKCircle) {
      actions.push({
         payload: {
            eventData: {
               state: 'none',
               isExpanded: true,
            },
            eventName: 'IDENTITY_PROOF',
         },
         type: 'nativeEventEmitter',
      });
   }

   onUserAction?.({
      actions,
   });
};

export const handleThankyouPage = ({ additionalData, onUserAction, getState }: any) => {
   let search = window.location.search;
   let params = new URLSearchParams(search);
   let requestID = params.get('requestID');

   const subtitle = {
      multiText: [
         {
            text: 'Request ID : ',
         },
         {
            text: requestID,
            color: '#027bfc',
            isBold: true,
         },
         {
            text: '\n We will keep you updated regarding its status.',
         },
      ],
      style: {
         marginTop: 7,
      },
      textAlignment: 'center',
   };
   const actions: any[] = [];
   if (requestID) {
      actions.push({
         payload: {
            eventData: {
               subtitle,
            },
            shouldResolveData: true,
            eventName: 'statusCardThankyou',
         },
         type: 'nativeEventEmitter',
      });
   } else {
      actions.push({
         type: 'navigation',
         payload: {
            id: 'redirection',
            screenName: '/reverification',
            shouldAddSearchParams: true,
            params: {},
            type: 'replace',
         },
      });
   }

   return onUserAction?.({
      actions,
   });
};

export const getFullPageLoaderAction = (show: boolean) => {
   return {
      type: 'fullPageLoader',
      payload: {
         show,
      },
   };
};

export const getNativeEventEmitterAction = (eventName: string, eventData: any) => {
   return {
      type: 'nativeEventEmitter',
      payload: {
         eventName,
         eventData,
      },
   };
};

export const getStoreValuesAction = (context: string, data: any) => {
   return {
      type: 'storeValuesInRedux',
      payload: {
         context,
         data,
      },
   };
};

export const getCallUtilFunction = (utilName: string, args: any[]) => {
   return {
      payload: {
         utilName,
         args,
      },
      type: 'callUtilFunction',
   };
};

export const handlePOISameAsPOA = ({ additionalData, onUserAction, getState }: any) => {
   const { checked } = additionalData?.dataFromWidget ?? {};

   const actions: any[] = [
      {
         type: 'storeValuesInRedux',
         payload: {
            context: 'identityProofData',
            data: {
               isPOASameAsPOI: checked,
            },
         },
      },
   ];

   if (checked) {
      const args = {
         additionalData: {
            dataFromWidget: {},
         },
         onUserAction,
         args: [] as string[],
         getState,
      };
      const imageStore = ImageStore.getInstance();

      args.additionalData.dataFromWidget = {
         type: 'identityFront',
         file: imageStore.getItem('addressFront'),
      };
      args.args = ['uploadFrontIdProof', 'true'];
      uploadPOAAndPOIProof(args);

      args.additionalData.dataFromWidget = {
         type: 'identityBack',
         file: imageStore.getItem('addressBack'),
      };
      args.args = ['uploadBackIdProof', 'true'];
      uploadPOAAndPOIProof(args);
   }

   onUserAction?.({
      actions,
   });
};

export const redirectToOrderSummaryPage = ({
   additionalData,
   onUserAction,
   getState,
}: any) => {
   const actions: any[] = [];

   const { storeValues } = getState();
   const lob = storeValues?.appData?.lob;

   if (lob?.toLowerCase() === 'postpaid') {
      const navigationActionObj = {
         payload: {
            delay: 0,
            screenName: '/order-summary',
            type: 'push',
            shouldAddSearchParams: true,
            params: {},
         },
         type: 'navigation',
      };
      actions?.push(navigationActionObj);
   } else {
      actions?.push({
         payload: {
            args: [
               {
                  isReferee: false,
               },
            ],
            utilName: 'callSendRefereeAndSubmitOtp',
         },
         type: 'callUtilFunction',
      });
   }

   return onUserAction?.({
      actions,
   });
};

export const deAllocateNumber = ({ additionalData, onUserAction, getState }: any) => {
   const { storeValues } = getState();
   const allocatedNumber = storeValues?.allocatedContext?.allocatedNumber;
   const selectedNumber = storeValues?.numberContext?.selectedNumber;
   const imsiNo = storeValues?.allocatedContext?.imsiNo;
   const lob = storeValues?.appData?.lob;
   const circleId = storeValues?.pincodeContext?.pincodeDetails?.circleId;

   const actions: any[] = [];

   if (!!allocatedNumber && allocatedNumber !== selectedNumber) {
      actions.push({
         type: 'apiCall',
         payload: {
            apiUrl: 'guardian/api/skyc/new/number-status/v1',
            requestType: 'POST',
            body: {
               msisdn: allocatedNumber,
               IMSI: imsiNo,
               lob,
               operationType: 'NUMBER_DEALLOCATE',
               isModal: false,
               circleId,
            },
            header: {},
         },
      });
   }

   return onUserAction?.({
      actions,
   });
};

export const validatePortNumber = ({ additionalData, onUserAction, getState }: any) => {
   const { storeValues } = getState();
   const msisdn = storeValues?.appData?.msisdn;
   const portNumber = storeValues?.numberContext?.selectedNumber;
   const pincode = storeValues?.pincodeContext?.pincodeDetails?.pincode;
   const lob = storeValues?.appData?.lob;

   const actions: any[] = [];

   if (portNumber === msisdn) {
      actions?.push({
         payload: {
            eventData: {
               inputsDataProps: {
                  mobileNumber: {
                     hintText:
                        'This number cannot be same as the alternate number. Try with another number.',
                     state: 'error',
                  },
               },
            },
            eventName: 'PortingDetailsWidget',
         },
         type: 'nativeEventEmitter',
      });
      actions?.push({
         analyticsAction: 'impression',
         analyticsData: {
            eVar21: '{{storeValues:appData.loggedInNumber}}',
            eventCategory: 'NEW_ACQ_POSTPAID_SKYC_LANDING_PAGE',
            customValue: 'This number cannot be same as the alternate number. Try with another number.',
            city: '{{storeValues:pincodeContext.pincodeDetails.city}}',
            eVar36: '{{storeValues:appData.msisdn}}',
            eVar25: '{{storeValues:pincodeContext.pincodeDetails.circleName}}',
            prop9: '{{storeValues:appData.lob}}',
            section: 'Step3_porting',
            eventAction: 'click',
            eventLabel: 'add porting details',
            prop4: 'SKYC',
            eventValue: portNumber,
            extraData11: '{{storeValues:layoutContext.layout}}',
            extraData12: '{{storeValues:orderData.orderData.cartItemId}}',
            subSection: 'PortingDetailsWidget',
            prop35: '{{storeValues:orderData.orderData.cartId}}',
            prop45: '{{storeValues:appData.subJourney}}',
            eVar10: 'error',
         },
      });
   } else {
      actions?.push({
         payload: {
            requestType: 'POST',
            apiUrl: 'guardian/api/skyc/new/fetch-mnp-details/v1',
            authTokenGroup: 'SKYC_REVERIFICATION',
            body: {
               siNumber: portNumber,
               pincode: pincode,
               lob,
            },
         },
         type: 'apiCall',
      });
   }

   return onUserAction?.({
      actions,
   });
};

export const onSubRadioPressUtil = ({ args, onUserAction, getState }: any) => {
   const subJourney: string = args[0];
   let isMnp = subJourney?.includes('port');
   const actions: any[] = [];

   const analyticsActionObj = {
      analyticsAction: 'impression',
      analyticsData: {
         eVar21: '{{storeValues:appData.loggedInNumber}}',
         eventCategory: 'new_acq_postpaid_skyc_landing_page',
         section: 'step2',
         subSection: 'UserDetailsWidget',
         eventAction: 'pageloaded',
         prop45: isMnp ? 'mnp' : 'new connection',
         prop4: 'SKYC',
         prop9: '{{storeValues:appData.lob}}',
      },
      id: 'user-details-load',
   };

   actions?.push(analyticsActionObj);

   return onUserAction?.({
      actions,
   });
};

export const onDatePickedUtil = ({ args, onUserAction, getState }: any) => {
   const { storeValues } = getState();
   const expiryDateReceived: string = args[1];
   const actions: any[] = [];
   //generationDate calculate
   //get circle from redux
   const donorCircleCode = storeValues?.portingContext?.previousCircleCode;
   const generationDate = new Date(expiryDateReceived.toString());
   const expiryDate = new Date(expiryDateReceived.toString());

   if (donorCircleCode == '117' || donorCircleCode == '122' || donorCircleCode == '123') {
      // if JK & NESA -> do this
      generationDate.setDate(expiryDate.getDate() - 15);
   } else {
      // else do that
      generationDate.setDate(expiryDate.getDate() - 4);
   }

   //format generated Date
   const upcMonth: number = generationDate.getMonth() + 1;
   const upcGeneratedDate: string =
      generationDate.getDate() + '/' + upcMonth + '/' + generationDate.getFullYear();

   //Store generation date in redux through actions
   const upcDateStoreAction = {
      payload: {
         data: {
            upcGeneratedDate: upcGeneratedDate,
         },
         context: 'portingContext',
      },
      type: 'storeValuesInRedux',
   };

   //Store expiry date in redux through actions

   //Analytics if needed

   // const analyticsActionObj = {
   //    "analyticsAction": "impression",
   //    "analyticsData": {
   //       "eVar21": "{{storeValues:appData.loggedInNumber}}",
   //       "eventCategory": "new_acq_postpaid_skyc_landing_page",
   //       "section": "step2",
   //       "subSection": "UserDetailsWidget",
   //       "eventAction": "pageloaded",
   //       "prop45": isMnp ? "mnp" : "new connection",
   //       "prop4": "SKYC"
   //    },
   //    "id": "user-details-load"
   // }

   actions?.push(upcDateStoreAction);

   return onUserAction?.({
      actions,
   });
}

export const onUpdateCartUserDetails = ({ args, onUserAction, getState }: any) => {
   const { storeValues } = getState();
   const actions: any[] = [];
   const uniqueReferenceId = new Date().getTime().toString()

   actions?.push({
      payload: {
         requestType: 'POST',
         apiUrl: 'guardian/api/skyc/new/updateCart/v1',
         authTokenGroup: 'SKYC_REVERIFICATION',
         body: {
            data: {
            orderSubType: storeValues?.appData?.orderSubType,
            contactMedium: [
                {
                    mediumType: "alternateNumber",
                    characteristic: {
                        phoneNumber: storeValues?.appData?.msisdn
                    }
                },
                {
                    mediumType: "email",
                    characteristic: {
                        emailAddress: storeValues?.appData?.email
                    }
                }
            ],
            milestone: "USER_DETAILS",
            channel: [
                {
                    name: "SHOP"
                }
            ],
            id: storeValues?.orderData?.orderData?.cartId,
            cartItem: [
                {
                    contactMedium: [
                        {
                            mediumType: "billingAddress",
                            characteristic: {
                                city: "",
                                postCode: ""
                            }
                        }
                    ],
                    product: {
                        productCharacteristic: [
                            {
                                name: "uniqueReferenceId",
                                value: {
                                    textValue: uniqueReferenceId
                                }
                            },
                            {
                                name: "productType",
                                value: {
                                    textValue: storeValues?.appData?.lob
                                }
                            },
                            {
                                name: "orderSubType",
                                value: {
                                    textValue: storeValues?.appData?.orderSubType
                                }
                            },
                            {
                                name: "acquisitionType",
                                value: {
                                    textValue: "SKYC"
                                }
                            },
                            {
                                name: "circleName",
                                valueType: "textValue",
                                value: {
                                    textValue: ""
                                }
                            },
                            {
                                name: "circleId",
                                valueType: "textValue",
                                value: {
                                    textValue: ""
                                }
                            }
                        ],
                        realizingResource: []
                    },
                    quantity: 1,
                    action: "add",
                    id: storeValues?.orderData?.orderData?.cartItemId,
                }
            ]
         },
         headers: {}
      }},
      type: 'apiCall',
   });

   return onUserAction?.({
      actions,
   });
};

export const showLinkListItemBasedOnLob = ({
   additionalData,
   onUserAction,
   getState,
}: any) => {
   let search = window.location.search;
   let params = new URLSearchParams(search);
   let lob = params.get('lob');
   let subLob = params.get('subLob');
   const actions: any[] = [];
   if (lob?.toUpperCase() === 'PREPAID') {
      actions.push({
         payload: {
            eventData: {
               hideOnPlatforms: [],
            },
            shouldResolveData: true,
            eventName: 'prepaidRechargeLinkListItemWidget',
         },
         type: 'nativeEventEmitter',
      });
   }

   if (subLob?.toUpperCase() == "MNP") {
      actions.push({
         payload: {
            eventData: {
               heading: "Activation takes up to 2 days"
            },
            shouldResolveData: true,
            eventName: 'activateLinkListItemWidget',
         },
         type: 'nativeEventEmitter',
      });
   }
   
   return onUserAction?.({
      actions,
   });

}

export const onGuardianNameChange = ({ args, onUserAction, getState }: any) => {
   const { storeValues } = getState();
   const aadhaarName = storeValues?.kycDetails?.aadhaarName;
   const guardianName : string = args[0];
   const actions: any[] = [];
   
   if (guardianName.toLowerCase() === aadhaarName.toLowerCase()) {
      actions?.push({
         payload: {
            eventData: {
               //removeHintTextOnChange: true,
               "inputsDataProps.guardianName.hintText": 'The guardian name should not be same as your name. Please enter a different name.',
               "inputsDataProps.guardianName.state": 'error',
               "inputsDataProps.guardianName.value": guardianName
            },
            eventName: 'step3Widget',
         },
         type: 'nativeEventEmitter',
      });
   } else {
      actions?.push({
         payload: {
            eventData: {
               //removeHintTextOnChange: true,
               "inputsDataProps.guardianName.hintText": '',
               "inputsDataProps.guardianName.state": 'none',
               "inputsDataProps.guardianName.value": guardianName
            },
            eventName: 'step3Widget',
         },
         type: 'nativeEventEmitter',
      });
   }

   return onUserAction?.({
      actions,
   });
}

export const onRefreeNameChange = ({ args, onUserAction, getState }: any) => {
   const { storeValues } = getState();
   const aadhaarName = storeValues?.kycDetails?.aadhaarName;
   const refreeName : string = args[0];
   const actions: any[] = [];
   
   if (refreeName.toLowerCase() === aadhaarName.toLowerCase()) {
      actions?.push({
         payload: {
            eventData: {
               //removeHintTextOnChange: true,
               "inputsDataProps.name.hintText": 'The Refree name should not be same as your name. Please enter a different name.',
               "inputsDataProps.name.state": 'error',
               "inputsDataProps.name.isValid": false,
            },
            eventName: 'LOCAL_REFERENCE_DETAILS',
         },
         type: 'nativeEventEmitter',
      });
      actions?.push(
         {
            "payload": {
              "eventData": {
                "leftCta": {
                  "disabled": true
                }
              },
              "eventName": "submitButton"
            },
            "type": "nativeEventEmitter"
          }
      )
   } else {
      actions?.push({
         payload: {
            eventData: {
               //removeHintTextOnChange: true,
               "inputsDataProps.name.hintText": '',
               "inputsDataProps.name.state": 'none',
               "inputsDataProps.name.isValid": true,
            },
            eventName: 'LOCAL_REFERENCE_DETAILS',
         },
         type: 'nativeEventEmitter',
      });
   }
   return onUserAction?.({
      actions,
   });
}

export const fallbackRedirectionUtil = ({args}: any) => {
   const url = args[0]
   
   const timeout = setTimeout(() => {
      window.location.replace(url)
   }, 15000);
   
   return () => clearTimeout(timeout);
}

export const handleVerificationPageOnload = ({ args, onUserAction, getState }: any) =>{
   const { storeValues } = getState();
   const msisdn = storeValues?.appData?.msisdn;
   const lob = storeValues?.appData?.lob;
   const actions: any[] = [];
   if (!msisdn && !lob) {
    actions?.push(
      {
         type: 'navigation',
         payload: {
            screenName: '/details',
            shouldAddSearchParams: true,
            params: {
               screenQueryParams: {
               },
            },
            type: 'replace',
         },
      },
    )
   }

   return onUserAction?.({
      actions,
   });
}